<script setup>
import { ref, onMounted } from 'vue'
import { generateToken } from '../utils/auth'
import { fetchUrlSources } from '../utils/common'

const emit = defineEmits(['next-step'])

// 状态变量
const models = ref([])
const selectedModel = ref('')
const baseImageUrl = ref('')
const baseImagePreview = ref('')
const baseImageFaces = ref([])
const swapImageUrls = ref([])
const loading = ref(false)
const taskStatus = ref('')
const resultImage = ref('')
const token = ref('')
const sourcePrefix = ref('素材模板/图片')
const sourceUrls = ref([])

// 获取token
function getToken() {
  token.value = generateToken()
}

// 获取模型列表
async function fetchModels() {
  try {
    const response = await fetch('/mhapi/v1/face/models', {
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json()
    models.value = data.data
    taskStatus.value = '获取模型列表成功'
  } catch (error) {
    console.error('获取模型列表失败:', error)
    taskStatus.value = '获取模型列表失败: ' + error.message
  }
}

// 检测底图人脸
async function detectBaseFaces() {
  if (!baseImageUrl.value) {
    alert('请输入底图URL')
    return
  }
  
  // 检测人脸
  try {
    loading.value = true
    taskStatus.value = '正在检测人脸...'
    
    const response = await fetch('/mhapi/v1/face/detect', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ img_url: baseImageUrl.value })
    })
    
    const data = await response.json()
    baseImagePreview.value = baseImageUrl.value
    pollDetectResult(data.task_id)
  } catch (error) {
    console.error('人脸检测失败:', error)
    taskStatus.value = '人脸检测失败: ' + error.message
    loading.value = false
  }
}

// 轮询检测结果
async function pollDetectResult(taskId) {
  let isPolling = true
  const checkResult = async () => {
    if (!isPolling) return
    
    try {
      const response = await fetch(`/mhapi/v1/face/detect/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      })
      const data = await response.json()
      
      if (data.task.state === 1) {
        baseImageFaces.value = data.task.faces
        taskStatus.value = `检测到 ${data.task.faces.length} 张人脸`
        loading.value = false
        isPolling = false
      } else {
        setTimeout(checkResult, 1000)
      }
    } catch (error) {
      console.error('检查任务状态失败:', error)
      taskStatus.value = '检查任务状态失败'
      loading.value = false
      isPolling = false
    }
  }
  
  checkResult()
}

// 执行换脸
async function swapFaces() {
  if (!baseImageUrl.value || !selectedModel.value) {
    alert('请上传底图和换脸图片')
    return
  }

  loading.value = true
  taskStatus.value = '正在换脸...'

  try {
    const requestBody = {
      model_id: selectedModel.value,
      base_img: {
        url: baseImageUrl.value
      },
      faces: swapImageUrls.value.map((url, index) => ({
        url,
        index,
        base_index: index
      }))
    }

    const response = await fetch('/mhapi/v1/face/imggen', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    const data = await response.json()
    pollSwapResult(data.task_id)
  } catch (error) {
    console.error('换脸失败:', error)
    taskStatus.value = '换脸失败: ' + error.message
    loading.value = false
  }
}

// 轮询换脸结果
async function pollSwapResult(taskId) {
  const checkResult = async () => {
    try {
      const response = await fetch(`/mhapi/v1/face/imggen/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      })
      const data = await response.json()
      
      if (data.task.state === 1) {
        resultImage.value = data.task.img_raw_url
        taskStatus.value = '换脸完成'
        loading.value = false
        handleSwapSuccess()
      } else {
        setTimeout(checkResult, 1000)
      }
    } catch (error) {
      console.error('检查任务状态失败:', error)
      taskStatus.value = '检查任务状态失败'
      loading.value = false
    }
  }
  
  checkResult()
}

// 组件加载时获取token和模型列表
onMounted(() => {
  getToken()
  fetchModels()
})

// 在换脸成功后调用
async function handleSwapSuccess() {
  // 换脸成功后，触发下一步
  emit('next-step')
}

// 添加新方法:
async function loadSourceImages() {
  try {
    loading.value = true
    taskStatus.value = '正在加载素材库...'
    
    const urls = await fetchUrlSources(sourcePrefix.value)
    sourceUrls.value = urls
    taskStatus.value = `已加载 ${urls.length} 张素材图片`
  } catch (error) {
    console.error('加载素材库失败:', error)
    taskStatus.value = '加载素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

function selectSourceImage(url, type) {
  if (type === 'base') {
    baseImageUrl.value = url
    detectBaseFaces()
  } else {
    swapImageUrls.value[type] = url
  }
}
</script>

<template>
  <div class="video-gen-container">
    <div class="video-gen">
      <div class="section">
        <h2>AI 换脸</h2>
        
        <div class="control-panel">
          <div class="input-group">
            <label>选择模型:</label>
            <select v-model="selectedModel">
              <option value="">请选择模型</option>
              <option v-for="model in models" :key="model.id" :value="model.id">
                {{ model.name }}
              </option>
            </select>
            <span class="model-desc" v-if="selectedModel && models.find(m => m.id === selectedModel)">
              {{ models.find(m => m.id === selectedModel).description }}
            </span>
          </div>

          <div class="upload-section">
            <div class="base-image">
              <h3>底图</h3>
              <div class="upload-container">
                <div class="url-input-group">
                  <input 
                    type="text" 
                    v-model="baseImageUrl" 
                    placeholder="输入底图URL"
                    @change="detectBaseFaces"
                    class="url-input"
                  >
                  <div class="source-controls">
                    <input 
                      type="text" 
                      v-model="sourcePrefix"
                      placeholder="输入素材库前缀"
                      class="prefix-input"
                    >
                    <button 
                      @click="loadSourceImages"
                      :disabled="loading"
                      class="source-button"
                    >
                      加载素材
                    </button>
                  </div>
                </div>
                
                <!-- 素材预览网格 -->
                <div v-if="sourceUrls.length" class="source-grid">
                  <div 
                    v-for="(url, index) in sourceUrls" 
                    :key="index" 
                    class="source-item"
                    @click="selectSourceImage(url, 'base')"
                  >
                    <img :src="url" :alt="`素材 ${index + 1}`">
                  </div>
                </div>

                <div class="preview" v-if="baseImagePreview">
                  <img :src="baseImagePreview" class="preview-image">
                </div>
              </div>
            </div>

            <div class="swap-images" v-if="baseImageFaces.length">
              <div v-for="(face, index) in baseImageFaces" :key="index" class="swap-image-item">
                <h3>换脸图片 {{ index + 1 }}</h3>
                <div class="upload-container">
                  <div class="url-input-group">
                    <input 
                      type="text" 
                      v-model="swapImageUrls[index]" 
                      placeholder="请输入换脸图片URL"
                      class="url-input"
                    >
                    <div class="source-controls">
                      <input 
                        type="text" 
                        v-model="sourcePrefix"
                        placeholder="输入素材库前缀"
                        class="prefix-input"
                      >
                      <button 
                        @click="loadSourceImages"
                        :disabled="loading"
                        class="source-button"
                      >
                        加载素材
                      </button>
                    </div>
                  </div>
                  
                  <!-- 素材预览网格 -->
                  <div v-if="sourceUrls.length" class="source-grid">
                    <div 
                      v-for="(url, sIndex) in sourceUrls" 
                      :key="sIndex" 
                      class="source-item"
                      @click="selectSourceImage(url, index)"
                    >
                      <img :src="url" :alt="`素材 ${sIndex + 1}`">
                    </div>
                  </div>
                  
                  <div class="preview" v-if="swapImageUrls[index]">
                    <img :src="swapImageUrls[index]" class="preview-image">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button 
            @click="swapFaces" 
            :disabled="loading"
          >
            {{ loading ? '处理中...' : '开始换脸' }}
          </button>
        </div>

        <pre class="status-log" v-if="taskStatus">{{ taskStatus }}</pre>

        <div class="result-image" v-if="resultImage">
          <h3>换脸结果</h3>
          <img :src="resultImage" alt="换脸结果">
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.video-gen-container {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  box-sizing: border-box;
  border-radius: 16px;
}

.video-gen {
  margin: 0 auto;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
  backdrop-filter: blur(5px);
  animation: fadeIn 0.5s ease-out;
}

.section {
  margin-bottom: 30px;
  padding: 25px;
  background: var(--section-bg);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  margin-top: 30px;
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

label {
  font-weight: 500;
  color: var(--text-color);
}

select {
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
  cursor: pointer;
}

select:hover {
  border-color: var(--primary-color);
}

select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

h2 {
  color: var(--text-color);
  margin-bottom: 20px;
  font-size: 1.8em;
  font-weight: 600;
  background: linear-gradient(45deg, var(--gradient-start), var(--gradient-end));
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

h3 {
  color: var(--text-color);
  margin-bottom: 15px;
  font-size: 1.2em;
}

.upload-section {
  display: flex;
  gap: 20px;
  margin: 30px 0;
}

.base-image,
.swap-images {
  flex: 1;
  min-width: 0;
}

.swap-image-item {
  margin-bottom: 30px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.upload-container {
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-container:hover {
  border-color: var(--primary-color);
  background: var(--section-bg);
}

.upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  cursor: pointer;
}

.file-input {
  display: none;
}

.preview-image {
  max-width: 100%;
  max-height: 150px;
  border-radius: 8px;
  object-fit: contain;
}

.status-log {
  margin: 15px 0;
  padding: 15px;
  background: var(--log-bg);
  color: var(--log-text);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9em;
  line-height: 1.5;
  border: 1px solid var(--border-color);
}

button {
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  align-self: flex-start;
}

button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--focus-shadow);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.result-image {
  margin-top: 40px;
  text-align: center;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.result-image img {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 4px var(--card-shadow);
  object-fit: contain;
}

.model-desc {
  font-size: 0.9em;
  color: var(--subtitle-color);
  margin-top: 4px;
  font-style: italic;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.url-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

.url-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.image-preview {
  margin-top: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 10px;
  background: var(--input-bg);
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

.source-controls {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.prefix-input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text-color);
}

.source-button {
  white-space: nowrap;
  padding: 8px 16px;
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: var(--section-bg);
  border-radius: 8px;
}

.source-item {
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.source-item:hover {
  border-color: var(--primary-color);
  transform: scale(1.05);
}

.source-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style> 